import { useId } from "@reach/auto-id"
import { forwardRef } from "react"
import PhoneInput from "react-phone-input-2"
import de from "react-phone-input-2/lang/de.json"

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormMessage,
  Icon,
} from "@trueskin-web/components"
import { useThemeUI } from "@trueskin-web/theme"
import { crossCircleIcon } from "@trueskin-web/theme/icons/crossCircleIcon"

const PhoneNumberInput = forwardRef(
  (
    {
      input,
      meta,
      required,
      label,
      placeholder,
      message,
      className,
      dataTestId,
      clearable,
      onClear,
      ...props
    },
    ref
  ) => {
    const id = useId()
    const {
      theme: { colors },
    } = useThemeUI()

    const isInvalid =
      (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) &&
      meta.touched

    return (
      <FormControl
        isInvalid={isInvalid}
        isRequired={required}
        className={className}
        data-testid={dataTestId}
        sx={{
          "& .react-tel-input": {
            border: "1px solid",
            borderRadius: "8px",
            borderColor: isInvalid
              ? colors.statusNegativeDark
              : colors.brandNeutralsMediumGrey,
            transition: "all .1s",
            borderWidth: isInvalid ? "2px" : "1px",
            ".selected-flag": {
              pl: 0,
              pr: 1,
              width: 50,
              display: "flex",
              justifyContent: "center",
              "&:hover, &:focus": {
                backgroundColor: "transparent",
                borderRadius: 0,
              },
            },
            "&.open": {
              ".selected-flag": {
                borderRadius: "8px",
              },
            },
          },
        }}
      >
        {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
        <div sx={{ position: "relative" }}>
          <PhoneInput
            id={id}
            localization={de}
            placeholder={placeholder}
            inputStyle={{
              width: "100%",
              height: 40,
              paddingLeft: 56,
              fontSize: "14px",
              lineHeight: "body",
              border: "none",
              borderRadius: "8px",
              transition: "0.2s ease",
            }}
            buttonStyle={{
              borderRadius: "8px 0 0 8px",
              backgroundColor: "transparent",
              border: "none",
            }}
            ref={ref}
            {...input}
            {...props}
          />
          {clearable && onClear && (
            <div
              data-testid="web-portal-phone-number-clear-btn"
              sx={{
                position: "absolute",
                top: 0,
                right: "10px",
                bottom: 0,
                width: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => onClear()}
            >
              <Icon
                icon={crossCircleIcon}
                size="sm"
                color={colors.brandNeutralsDarkGrey}
                sx={{ cursor: "pointer" }}
              />
            </div>
          )}
        </div>
        <FormErrorMessage
          sx={{
            "& > div": {
              display: "inline-flex",
              alignItems: "center",
            },
          }}
        >
          {meta.error || meta.submitError}
        </FormErrorMessage>
        <FormMessage sx={{ color: "brandNeutralsDarkGrey" }}>
          {message}
        </FormMessage>
      </FormControl>
    )
  }
)

PhoneNumberInput.displayName = "PhoneNumberInput"

export default PhoneNumberInput
